import { CollectionAgingLine, CollectionClient, CollectionInvoiceDocument } from '../model';
import { matchesDepartmentGroup } from '../../shared/departmentGroup/departmentGroup';

export type ToneLabel = 'Past Due' | 'Friendly' | 'Second Notice' | 'Urgent';

// Match the backend CandidatesBuilder fallbacks (`|| 60` / `|| 90`) so a missing config
// classifies tone identically on the frontend.
export const DEFAULT_PAST_DUE_THRESHOLD_DAYS = 30;
export const DEFAULT_FRIENDLY_THRESHOLD_DAYS = 60;
export const DEFAULT_SECOND_NOTICE_THRESHOLD_DAYS = 90;

export interface ScopeThresholds {
  pastDueThresholdDays: number;
  friendlyThresholdDays: number;
  secondNoticeThresholdDays: number;
}

interface ScopedTotals {
  total_past_due: number;
  total_debit: number;
  total_credit: number;
  worst_aging_days: number;
  invoice_count: number;
  credit_only: boolean;
}

// Reuses the client-level predicate so specific group / __mine__ / __none__ / all stay in one place.
const matchesDepartmentGroups = (departmentGroups: string[], departmentGroup: string, myGroups: string[]): boolean =>
  matchesDepartmentGroup(
    { department_groups: departmentGroups, has_ungrouped_invoices: departmentGroups.length === 0 },
    departmentGroup,
    myGroups,
  );

export const lineMatchesDepartmentGroup = (
  line: CollectionAgingLine,
  departmentGroup: string,
  myGroups: string[],
): boolean => matchesDepartmentGroups(line.department_groups, departmentGroup, myGroups);

export const filterInvoiceDocumentsByDepartment = (
  documents: CollectionInvoiceDocument[],
  departmentGroup: string,
  myGroups: string[],
): CollectionInvoiceDocument[] =>
  documents.filter((doc) => matchesDepartmentGroups(doc.department_groups, departmentGroup, myGroups));

// Mirrors Collections::CollectionCandidate#compute_totals. Debit/credit split on the SIGN of each
// line's past due; count/worst-aging/credit_only key off has_positive (the backend's
// has_positive_past_due?), which can differ from the sign in mixed-bucket lines.
export const computeScopedTotals = (lines: CollectionAgingLine[]): ScopedTotals => {
  const totalPastDue = lines.reduce((sum, line) => sum + line.total_past_due, 0);
  const totalDebit = lines
    .filter((line) => line.total_past_due > 0)
    .reduce((sum, line) => sum + line.total_past_due, 0);
  const totalCredit = lines
    .filter((line) => line.total_past_due < 0)
    .reduce((sum, line) => sum + line.total_past_due, 0);
  const positives = lines.filter((line) => line.has_positive);
  const worstAgingDays = positives.reduce((max, line) => Math.max(max, line.aging_days), 0);

  return {
    total_past_due: totalPastDue,
    total_debit: totalDebit,
    total_credit: totalCredit,
    worst_aging_days: worstAgingDays,
    invoice_count: positives.length,
    credit_only: positives.length === 0,
  };
};

// Mirrors Collections::CollectionCandidate#tone thresholds.
export const computeTone = (
  worstAgingDays: number,
  pastDueThresholdDays: number,
  friendlyThresholdDays: number,
  secondNoticeThresholdDays: number,
): ToneLabel => {
  if (worstAgingDays > secondNoticeThresholdDays) return 'Urgent';
  if (worstAgingDays > friendlyThresholdDays) return 'Second Notice';
  if (worstAgingDays > pastDueThresholdDays) return 'Friendly';
  return 'Past Due';
};

// Returns a client with all invoice-derived fields recomputed for the selected department, or null
// when nothing is past due in that department (scoped past due <= 0) — mirroring the candidates
// endpoint, which only lists accounts with a positive total_past_due.
export const scopeClient = (
  client: CollectionClient,
  departmentGroup: string,
  myGroups: string[],
  thresholds: ScopeThresholds,
): CollectionClient | null => {
  const lines = (client.aging_lines ?? []).filter((line) =>
    lineMatchesDepartmentGroup(line, departmentGroup, myGroups),
  );
  const totals = computeScopedTotals(lines);
  if (totals.total_past_due <= 0) return null;

  const invoiceDocuments = filterInvoiceDocumentsByDepartment(
    client.invoice_documents ?? [],
    departmentGroup,
    myGroups,
  );

  // Backend tone is null only when the client has no positive past-due line (no bucket).
  const { pastDueThresholdDays, friendlyThresholdDays, secondNoticeThresholdDays } = thresholds;
  const tone = client.tone
    ? computeTone(totals.worst_aging_days, pastDueThresholdDays, friendlyThresholdDays, secondNoticeThresholdDays)
    : null;

  // suppression_reason / toggle_allowed come from the backend overall tone; preserve it for tooltips.
  return { ...client, ...totals, overall_tone: client.tone, tone, invoice_documents: invoiceDocuments };
};
